/**
 * OpenAI Ads Measurement Pixel — browser half of Fleetwire's conversion tracking.
 *
 * The pixel itself (`oaiq('init', {pixelId})`) is rendered ONCE by
 * resources/views/spa.blade.php on every full page load; this module never
 * initializes it and never touches pixelId, so Vue routing and component
 * remounts cannot re-initialize it.
 *
 * Every event fired here was already confirmed by the server, which also
 * reported it through the Conversions API with the SAME event_id. OpenAI
 * deduplicates on Pixel ID + event name + event_id, so the browser and server
 * copies count once.
 */

export interface OpenAIAdsConversionEvent {
    name: string;
    event_id: string;
    data?: Record<string, unknown>;
}

export interface OpenAIAdsConversionPayload {
    user?: {
        email_sha256?: string;
        external_id_sha256?: string;
    };
    events?: OpenAIAdsConversionEvent[];
}

const trackedStorageKey = (event: OpenAIAdsConversionEvent): string => `openai_ads_${event.name}_${event.event_id}`;

const wasTracked = (event: OpenAIAdsConversionEvent): boolean => {
    try {
        return window.sessionStorage.getItem(trackedStorageKey(event)) === 'true';
    } catch {
        return false;
    }
};

const rememberTracked = (event: OpenAIAdsConversionEvent): void => {
    try {
        window.sessionStorage.setItem(trackedStorageKey(event), 'true');
    } catch {
        // Storage can be unavailable in privacy modes; event_id still deduplicates at OpenAI.
    }
};

/**
 * Fire the pixel for events the server confirmed. Safe to call from a component
 * that remounts or a page that is revisited: each event_id fires once per
 * session, and a missing SDK is a silent no-op — advertising measurement must
 * never interrupt signup.
 */
export const trackOpenAIAdsConversions = (payload: OpenAIAdsConversionPayload | null | undefined): void => {
    try {
        if (!payload || typeof window.oaiq !== 'function') {
            return;
        }

        const events = (payload.events ?? []).filter(
            (event) => !!event?.name && !!event?.event_id && !wasTracked(event),
        );

        if (events.length === 0) {
            return;
        }

        const user = {
            ...(payload.user?.email_sha256 ? {email_sha256: payload.user.email_sha256} : {}),
            ...(payload.user?.external_id_sha256 ? {external_id_sha256: payload.user.external_id_sha256} : {}),
        };

        // Documented: after the first init, pixelId may be omitted to update user data.
        if (Object.keys(user).length > 0) {
            window.oaiq('init', {user});
        }

        events.forEach((event) => {
            window.oaiq?.('measure', event.name, event.data ?? {type: 'customer_action'}, {event_id: event.event_id});
            rememberTracked(event);
        });
    } catch {
        // Never let measurement break the flow that produced the conversion.
    }
};
